import { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useAuth } from '../../hooks/useAuth';
import { Card } from '../../components/ui/Card';
import { Spinner } from '../../components/ui/Spinner';
import * as adminApi from '../../api/admin.api';
import type { AdminDashboardStats } from '../../types/admin';
import styles from './AdminDashboard.module.css';

interface ChartItem {
  name: string;
  revenue: number;
}

export function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState<AdminDashboardStats | null>(null);
  const [chartData, setChartData] = useState<ChartItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      adminApi.getDashboard(),
      adminApi.getWeeklyRevenue(4),
    ])
      .then(([dashStats, revenue]) => {
        setStats(dashStats);
        const formatted = revenue.map((item, i) => ({
          name: `Нед ${i + 1}`,
          revenue: item.revenue,
        }));
        setChartData(formatted.length > 0 ? formatted : [{ name: 'Нет данных', revenue: 0 }]);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (user?.role !== 'admin') {
    return (
      <div className={styles.page}>
        <div className={styles.accessDenied}>
          <p className={styles.accessDeniedTitle}>Доступ запрещён</p>
          <p>Эта страница доступна только администраторам</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0,
    }).format(value / 100);

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Админ-панель</h1>

      <div className={styles.statsGrid}>
        <Card className={styles.statCard}>
          <div className={`${styles.statIcon} ${styles.statIconPrimary}`}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{stats?.totalUsers ?? 0}</div>
            <div className={styles.statLabel}>Пользователи</div>
          </div>
        </Card>

        <Card className={styles.statCard}>
          <div className={`${styles.statIcon} ${styles.statIconSuccess}`}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{stats?.activeSubscriptions ?? 0}</div>
            <div className={styles.statLabel}>Активные подписки</div>
          </div>
        </Card>

        <Card className={styles.statCard}>
          <div className={`${styles.statIcon} ${styles.statIconWarning}`}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>
              {stats ? formatCurrency(stats.totalRevenue) : '0 ₽'}
            </div>
            <div className={styles.statLabel}>Общий доход</div>
          </div>
        </Card>

        <Card className={styles.statCard}>
          <div className={`${styles.statIcon} ${styles.statIconInfo}`}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" />
            </svg>
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{stats?.activeTrials ?? 0}</div>
            <div className={styles.statLabel}>Активные триалы</div>
          </div>
        </Card>
      </div>

      <div className={styles.chartSection}>
        <h2 className={styles.chartTitle}>Доход по неделям</h2>
        <Card className={styles.chartCard}>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="name" stroke="var(--text-secondary)" fontSize={12} />
              <YAxis
                stroke="var(--text-secondary)"
                fontSize={12}
                tickFormatter={(v: number) => `${v / 100} ₽`}
              />
              <Tooltip
                formatter={(value) => [`${(Number(value) / 100).toLocaleString('ru-RU')} ₽`, 'Доход']}
                contentStyle={{
                  backgroundColor: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  color: 'var(--text)',
                }}
              />
              <Bar dataKey="revenue" fill="var(--accent-primary)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}
